/**
 * Email Service for FieldNotes
 * 
 * Formats clinical notes and transcripts for email delivery.
 * Uses mailto: links for client-side sending (works on mobile).
 * In production, replace with Cloud Function + SendGrid/SES.
 */

/**
 * Format a clinical note as plain text for email
 */
export function formatNoteForEmail(note) {
  if (!note?.header) return "No note data available.";

  let text = `═══════════════════════════════════════════
  GENERAL PROGRESS NOTE — CLINICAL
═══════════════════════════════════════════

Individual: ${note.header.individual || "—"}
DOB: ${note.header.dob || "—"}
Date: ${note.header.date || "—"}
Clinician: ${note.header.clinician || "—"}
Location: ${note.header.location || "—"}
Site: ${note.header.site || "—"}
Service: ${note.header.serviceType || "—"}
Duration: ${note.header.duration || "—"}

───────────────────────────────────────────
TODAY'S PRESENTING ISSUES
───────────────────────────────────────────
${note.presenting || "Not documented"}

───────────────────────────────────────────
STATUS
───────────────────────────────────────────
Current Living Situation: ${note.livingStatus || "N/A"}
Suicide Risk: ${note.suicideRisk || "N/A"}
Medication Adherence: ${note.medicationAdherence || "N/A"}
Medical Conditions Addressed: ${note.medicalConditions || "N/A"}
`;

  if (note.interventions?.length > 0) {
    text += `
───────────────────────────────────────────
INTERVENTIONS DURING SESSION
───────────────────────────────────────────
${note.interventions.map((item, i) => `${i + 1}. ${item}`).join("\n")}
`;
  }

  text += `
───────────────────────────────────────────
RESPONSE TO INTERVENTIONS
───────────────────────────────────────────
${note.response || "Not documented"}

───────────────────────────────────────────
INDIVIDUAL'S PROGRESS
───────────────────────────────────────────
${note.progress || "Not documented"}
`;

  if (note.selfRating) text += `Self-Rating: ${note.selfRating}\n`;
  if (note.proudOf) text += `Most Proud Of: "${note.proudOf}"\n`;
  if (note.newProblems) text += `New Problems: ${note.newProblems}\n`;

  text += `
───────────────────────────────────────────
SERVICE RECOMMENDATION / PLAN
───────────────────────────────────────────
${note.servicePlan || "Not documented"}

───────────────────────────────────────────
BILLING
───────────────────────────────────────────
Code: ${note.billingCode || "—"}
Units: ${note.billingUnits || "—"}
AI Confidence: ${note.confidence || "—"}%

═══════════════════════════════════════════
Generated by FieldNotes AI · ${new Date().toLocaleString()}
═══════════════════════════════════════════`;

  return text;
}

/**
 * Format transcript for email
 */
export function formatTranscriptForEmail(transcript) {
  if (!transcript) return "No transcript available.";

  return `═══════════════════════════════════════════
  SESSION TRANSCRIPT
═══════════════════════════════════════════

${transcript}

═══════════════════════════════════════════
Transcript captured by FieldNotes · ${new Date().toLocaleString()}
═══════════════════════════════════════════`;
}

/**
 * Build email subject line
 */
export function buildEmailSubject(note) {
  const individual = note?.header?.individual || "Individual";
  const date = note?.header?.date || new Date().toLocaleDateString();
  const service = note?.header?.serviceType || "ICM Visit";
  return `FieldNotes: ${service} — ${individual} — ${date}`;
}

/**
 * Open email client with pre-populated note + transcript
 */
export function openEmailWithNote({ note, transcript, toEmail }) {
  const subject = buildEmailSubject(note);
  const noteText = formatNoteForEmail(note);
  const transcriptText = transcript ? formatTranscriptForEmail(transcript) : "";

  let body = noteText;
  if (transcriptText) {
    body += "\n\n\n" + transcriptText;
  }

  // mailto: has a ~2000 char limit on some clients
  // For longer notes, we truncate the transcript and keep the full note
  const maxLength = 8000;
  if (body.length > maxLength) {
    body = noteText + "\n\n[Transcript too long for email — use Copy Transcript button to paste separately]";
  }

  const mailto = `mailto:${encodeURIComponent(toEmail || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = mailto;
}

/**
 * Copy note + transcript to clipboard as formatted text
 */
export async function copyNoteAndTranscript({ note, transcript }) {
  const noteText = formatNoteForEmail(note);
  const transcriptText = transcript ? "\n\n" + formatTranscriptForEmail(transcript) : "";
  const fullText = noteText + transcriptText;

  try {
    await navigator.clipboard.writeText(fullText);
    return { success: true };
  } catch (e) {
    console.error("Copy failed:", e);
    return { success: false, error: e.message };
  }
}